import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import AuthService from "../../services/AuthService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { formatDate, formatTimeAgo } from '../../utils/helper';

const List = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState({});
    const [unreadCount, setUnreadCount] = useState(0);
    const [apiData, setApiData] = useState({
        search: "",
        is_read: "",
        page: 1,
    });
    
    const authUser = AuthService.getCurrentUser();
    const isCustomer = authUser && authUser.role === 'customer';
    const baseUrl = isCustomer ? 'customer/notifications' : 'admin/notifications';
    const homePath = isCustomer ? '/appointments' : '/admin/dashboard';

    useEffect(() => {
        getNotifications();
    }, [apiData.page, apiData.search, apiData.is_read]);

    const getNotifications = async () => {
        try {
            setIsLoader(true);
            const params = new URLSearchParams();
            if (apiData.search) params.append('search', apiData.search);
            if (apiData.is_read !== '') params.append('is_read', apiData.is_read);
            params.append('per_page', 15);
            params.append('page', apiData.page);

            const response = await ApiService.request({
                method: "GET",
                url: `${baseUrl}?${params.toString()}`,
            });
            const data = response.data;
            if (data.status) {
                setNotifications(data.data.notifications || []);
                setPagination(data.data.pagination || {});
                setUnreadCount(data.data.unread_count || 0);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            console.error('Error fetching notifications:', error);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            is_read: '',
            page: 1
        });
    };

    const markAsRead = async (notificationId) => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "POST",
                url: `${baseUrl}/${notificationId}/mark-read`,
            });
            const data = response.data;
            if (data.status) {
                getNotifications();
                toast.success(data.message || 'Notification marked as read');
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Mark all notifications as read?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: 'Yes',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            try {
                setIsLoader(true);
                const response = await ApiService.request({
                    method: "POST",
                    url: `${baseUrl}/mark-all-read`,
                });
                const data = response.data;
                if (data.status) {
                    getNotifications();
                    toast.success(data.message || 'All notifications marked as read');
                } else {
                    toast.error(data.message);
                }
                setIsLoader(false);
            } catch (error) {
                setIsLoader(false);
                console.error('Error marking all notifications as read:', error);
            }
        }
    };

    const clearAllNotifications = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete all!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            try {
                setIsLoader(true);
                const response = await ApiService.request({
                    method: "POST",
                    url: `${baseUrl}/clear-all`,
                });
                const data = response.data;
                if (data.status) {
                    getNotifications();
                    toast.success(data.message || 'All notifications cleared');
                } else {
                    toast.error(data.message);
                }
                setIsLoader(false);
            } catch (error) {
                setIsLoader(false);
                console.error('Error clearing notifications:', error);
            }
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0 text-capitalize">
                            Notifications
                            {unreadCount > 0 && (
                                <span className="badge badge-danger ms-2">{unreadCount}</span>
                            )}
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to={homePath} className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Notifications</li>
                        </ul>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input 
                                                    type="text" 
                                                    value={apiData.search} 
                                                    onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })} 
                                                    className="form-control form-control-solid me-5"
                                                    placeholder="Search notifications.."
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-select form-control-solid"
                                                value={apiData.is_read}
                                                onChange={(e) => setApiData({ ...apiData, is_read: e.target.value, page: 1 })}
                                            >
                                                <option value="">All Notifications</option>
                                                <option value="false">Unread</option>
                                                <option value="true">Read</option>
                                            </select>
                                        </div>
                                        <div className="col-md-5 text-end">
                                            <button
                                                type="button"
                                                className="btn btn-light-primary me-2"
                                                onClick={markAllAsRead}
                                                disabled={unreadCount === 0}
                                            >
                                                <i className="fas fa-check-double me-2"></i>Mark All as Read
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-light-danger"
                                                onClick={clearAllNotifications}
                                                disabled={notifications.length === 0}
                                            >
                                                <i className="fas fa-trash me-2"></i>Clear All
                                            </button>
                                        </div>
                                    </div>
                                    {(apiData.search || apiData.is_read !== '') && (
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light"
                                                    onClick={clearFilter}
                                                >
                                                    <i className="fas fa-times me-2"></i>Clear Filters
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <>
                                    {notifications.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table align-middle table-row-dashed fs-6 gy-5">
                                                <thead>
                                                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                        <th className="min-w-150px">Notification</th>
                                                        <th className="min-w-100px">Time</th>
                                                        <th className="min-w-100px text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="text-gray-600 fw-semibold">
                                                    {notifications.map((notification) => (
                                                        <tr 
                                                            key={notification.id}
                                                            className={!notification.is_read ? 'bg-light-warning' : ''}
                                                        >
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    {!notification.is_read && (
                                                                        <span className="badge badge-circle badge-danger w-8px h-8px mb-2"></span>
                                                                    )}
                                                                    <span className="text-dark fw-bold fs-5 mb-1">
                                                                        {notification.title}
                                                                    </span>
                                                                    <span className="text-gray-600 fs-6">
                                                                        {notification.message}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="text-muted fs-7">
                                                                    {formatDate(notification.created_at, 'MMM DD, YYYY')}
                                                                    <br />
                                                                    <small>{formatTimeAgo(notification.created_at)}</small>
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                {!notification.is_read && (
                                                                    <button
                                                                        className="btn btn-sm btn-light-primary me-2"
                                                                        onClick={() => markAsRead(notification.id)}
                                                                        title="Mark as read"
                                                                    >
                                                                        <i className="fas fa-check"></i>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-10">
                                            <i className="fas fa-bell-slash fs-3x text-muted mb-5"></i>
                                            <h3 className="text-muted">No notifications found</h3>
                                            <p className="text-muted">You're all caught up!</p>
                                        </div>
                                    )}
                                    <Paginator
                                        pagination={pagination}
                                        onPageChange={handlePageChange}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
